import { html, nothing, type TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import { renderExternalEmbedGate } from '../common/external-embed-gate.js';

export interface YoutubeVideoOptions {
  videoId: string;
  /** Proxied preview image (proxiedPreviewUrl), or null. */
  previewImageUrl: string | null;
  /** The reader released the video for this view. */
  loaded: boolean;
  onLoad: () => void;
  overlayHidden: boolean;
  printImageHidden: boolean;
}

/** youtube-nocookie sets no YouTube cookies before the video plays. */
export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

/**
 * Video area of the YouTube block: the external-content placeholder until
 * the reader loads the video, then the player.
 */
export function renderYoutubeVideo({
  videoId,
  previewImageUrl,
  loaded,
  onLoad,
  overlayHidden,
  printImageHidden,
}: YoutubeVideoOptions): TemplateResult {
  // Print and export show the proxied preview in both states.
  const printImage = previewImageUrl
    ? html`<img
        class=${classMap({
          'affine-embed-youtube-video-iframe-overlay': true,
          'media-print': true,
          hide: printImageHidden,
        })}
        src=${previewImageUrl}
        alt="YouTube Video"
        loading="lazy"
      />`
    : nothing;

  if (!loaded) {
    return html`<div class="affine-embed-youtube-video-iframe-container">
      ${renderExternalEmbedGate({
        provider: 'youtube',
        previewImageUrl,
        onLoad,
      })}
      ${printImage}
    </div>`;
  }

  return html`<div class="affine-embed-youtube-video-iframe-container">
    <iframe
      id="ytplayer"
      type="text/html"
      src=${youtubeEmbedUrl(videoId)}
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowfullscreen
      loading="lazy"
      credentialless
    ></iframe>

    <!-- overlay to prevent the iframe from capturing pointer events -->
    <div
      class=${classMap({
        'affine-embed-youtube-video-iframe-overlay': true,
        hide: overlayHidden,
      })}
    ></div>
    ${printImage}
  </div>`;
}
